import React, { useState, useRef, useEffect } from "react";
import {
  ComposableMap,
  Geographies,
  Geography
} from "react-simple-maps";
import features from "./features.json";
import { getDashboardStats } from './api';

// Color scale function
const getColor = (value, min, max) => {
  if (!value) return "var(--surface-2)"; // Light gray for countries without data

  // Adjust the gradient based on actual min and max
  const normalizedValue = (value - min) / (max - min);

  // Define color stops using brand palette
  const colors = {
    low: [229, 209, 143],   // Brand yellow (--color-accent)
    mid: [168, 54, 54],     // Brand red (--color-brand)
    high: [63, 0, 1]        // Brand dark red (--color-brand-dark)
  };

  let r, g, b;

  if (normalizedValue < 0.5) {
    // Interpolate between low and mid
    const t = normalizedValue * 2;
    r = Math.round(colors.low[0] + (colors.mid[0] - colors.low[0]) * t);
    g = Math.round(colors.low[1] + (colors.mid[1] - colors.low[1]) * t);
    b = Math.round(colors.low[2] + (colors.mid[2] - colors.low[2]) * t);
  } else {
    // Interpolate between mid and high
    const t = (normalizedValue - 0.5) * 2;
    r = Math.round(colors.mid[0] + (colors.high[0] - colors.mid[0]) * t);
    g = Math.round(colors.mid[1] + (colors.high[1] - colors.mid[1]) * t);
    b = Math.round(colors.mid[2] + (colors.high[2] - colors.mid[2]) * t);
  }

  return `rgb(${r}, ${g}, ${b})`;
};

const MapChart = ({ activeCountriesView }) => {
  const [tooltipContent, setTooltipContent] = useState("");
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [mapData, setMapData] = useState({});
  const mapRef = useRef(null);
  const [mapScale, setMapScale] = useState(90);

  // Scroll to top whenever the map view becomes active
  useEffect(() => {
    if (activeCountriesView === 'map' && mapRef.current) {
      mapRef.current.scrollTop = 0;
    }
  }, [activeCountriesView]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const stats = await getDashboardStats();
        console.log('Received stats:', stats);

        if (stats && stats.countryData) {
          const newData = {};

          // Correctly access the geometries array from features
          const geometries = features.objects.world.geometries || [];
          console.log('Available countries in features:',
            geometries.map(geo => geo.properties.name)
          );

          console.log('All backend country names:', stats.countryData.map(c => c.name));

          stats.countryData.forEach(country => {
            console.log('Processing country:', country.name);
            // Find the matching country code from features.json
            const countryFeature = geometries.find(
              geo => {
                const backendName = country.name.toLowerCase().replace(/\s+/g, ' ');
                const mapName = geo.properties.name.toLowerCase().replace(/\s+/g, ' ');

                // Handle special cases
                if (backendName === 'russian federation') return mapName === 'russia';
                if (backendName === 'lao pdr') return mapName === 'laos';
                if (backendName === 'korea, republic of') return mapName === 'south korea';
                if (backendName === 'vietnam') return mapName === 'vietnam';

                return backendName === mapName;
              }
            );

            if (countryFeature) {
              console.log(`Matched ${country.name} to ${countryFeature.id} (${countryFeature.properties.name})`);
              newData[countryFeature.id] = country.count;
            } else {
              console.log(`No match found for ${country.name}`);
            }
          });
          console.log('Final map data:', newData);
          setMapData(newData);
        }
      } catch (error) {
        console.error('Error fetching country data:', error);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (mapRef.current) {
        const width = mapRef.current.offsetWidth;
        // Adjust the multiplier as needed for your projection
        setMapScale(width * 0.15); // 0.15 empirically fits most world maps
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleMouseMove = (event) => {
    if (mapRef.current) {
      const rect = mapRef.current.getBoundingClientRect();
      const scrollTop = mapRef.current.scrollTop;
      const scrollLeft = mapRef.current.scrollLeft;

      setTooltipPosition({
        x: event.clientX - rect.left + scrollLeft,
        y: event.clientY - rect.top + scrollTop
      });
    }
  };

  // Calculate min and max values for color scaling
  const values = Object.values(mapData);
  const minValue = Math.min(...values);
  const maxValue = Math.max(...values);

  return (
    <div
      ref={mapRef}
      style={{
        position: "relative",
        width: "100%",
        height: "calc(100vh - 250px)",
        backgroundColor: "var(--surface-1)",
        border: "1px solid var(--border-color)",
        borderRadius: "8px",
        overflow: "hidden",
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)"
      }}
      onMouseMove={handleMouseMove}
    >
      <ComposableMap
        projectionConfig={{
          center: [-10, -10],
        }}
        style={{
          width: "80%",
          height: "80%",
          position: "relative"
        }}
        preserveAspectRatio="xMinYMin meet"
      >
        <Geographies geography={features}>
          {({ geographies }) =>
            geographies.map((geo) => {
              const value = mapData[geo.id] || 0;
              const color = getColor(value, minValue, maxValue);
              return (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  onMouseEnter={() => {
                    if (value) {
                      setTooltipContent(`${geo.properties.name}: ${value} projects`);
                    } else {
                      setTooltipContent(geo.properties.name);
                    }
                  }}
                  onMouseLeave={() => {
                    setTooltipContent("");
                  }}
                  style={{
                    default: {
                      fill: color,
                      stroke: "var(--border-color)",
                      outline: "none",
                      strokeWidth: 0.5,
                    },
                    hover: {
                      fill: value ? color : "var(--surface-2)",
                      stroke: "var(--primary)",
                      outline: "none",
                      strokeWidth: 1,
                      opacity: 0.95,
                    },
                    pressed: {
                      fill: value ? color : "var(--surface-2)",
                      stroke: "var(--primary-dark)",
                      outline: "none",
                      strokeWidth: 1,
                      opacity: 0.9,
                    },
                  }}
                />
              );
            })
          }
        </Geographies>
      </ComposableMap>
      {tooltipContent && (
        <div
          style={{
            position: "fixed",
            top: tooltipPosition.y,
            left: tooltipPosition.x,
            backgroundColor: "var(--surface-1)",
            color: "var(--color-text-dark)",
            padding: "8px 12px",
            borderRadius: "6px",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
            fontSize: "14px",
            fontWeight: "600",
            pointerEvents: "none",
            zIndex: 1000,
            minWidth: "120px",
            textAlign: "center",
            border: "1px solid var(--border-color)",
            backdropFilter: "blur(4px)",
            transition: "all 0.1s ease-out",
            transform: "translate(-50%, -100%)"
          }}
        >
          {tooltipContent}
        </div>
      )}
    </div>
  );
};

export default MapChart;